import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { formatDayTime, formatPhone, nowMs, withCountRu } from '@dorixona/utils'
import { Press } from '@dorixona/ui'
import { Icon } from '../../ui/Icon'
import { Modal } from '../../ui/Modal'
import { Failed, failureText } from '../../ui/Load'
import { TableSkeleton } from '../../ui/Skeleton'
import { Pagination } from '../../ui/Pagination'
import { PageActions } from '../../app/PageHeader'
import { Select, type SelectOption } from '../../ui/Select'
import { initials } from '../auth/model'
import { useSession } from '../auth/session'
import { useRoles } from '../roles/api'
import { useStores } from '../stores/api'
import { storeTitle, storeTitleById } from '../stores/model'
import { useDeleteStaff, useResetPassword, useSaveStaff, useStaffPage, type Staff } from './api'
import {
  EMPTY_FILTERS,
  PAGE_SIZES,
  filtersFromParams,
  filtersToParams,
  isFiltered,
  toStaffQuery,
  type PageSize,
  type StaffFilters,
} from './model'
import { IssuedPassword } from './IssuedPassword'
import { StaffStatus } from './StaffScreen'

export interface AdminsScreenProps {
  onToast: (message: string) => void
}

/**
 * Сотрудники админки: кто входит, с какой ролью и из какой аптеки.
 * Себя и владельца отключить нельзя — иначе можно остаться без доступа;
 * сервер это тоже проверяет и отвечает 409 `protected`.
 *
 * Список отбирает и листает сервер, по 10 строк; поиск, фильтры и страница —
 * в адресе, как в каталоге.
 */
export function AdminsScreen({ onToast }: AdminsScreenProps) {
  const { me, can } = useSession()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const filters = filtersFromParams(params)
  const [issued, setIssued] = useState<{ name: string; password: string } | null>(null)
  const [removing, setRemoving] = useState<Staff | null>(null)
  // Блокировку входа после промахов сравниваем с моментом открытия экрана:
  // список обновляется после каждого действия, секундная точность не нужна.
  const [now] = useState(() => nowMs())

  const staff = useStaffPage(toStaffQuery(filters))
  const roles = useRoles()
  const stores = useStores()
  const save = useSaveStaff()
  const reset = useResetPassword()
  const remove = useDeleteStaff()

  const canCreate = can('admins', 'c')
  const canUpdate = can('admins', 'u')
  const canDelete = can('admins', 'd')

  /**
   * Новый отбор начинается с первой страницы. Листание — шаг в истории
   * браузера, правка фильтра заменяет текущий: иначе каждая буква поиска
   * стала бы шагом «Назад».
   */
  function set(patch: Partial<StaffFilters>) {
    const paging = 'page' in patch
    setParams(filtersToParams({ ...filters, page: 1, ...patch }), { replace: !paging })
  }

  const filtered = isFiltered(filters)

  function resetFilters() {
    set({ ...EMPTY_FILTERS, size: filters.size })
  }

  function goTo(page: number) {
    set({ page })
    // Новая страница читается сверху: таблица под фильтрами.
    window.scrollTo({ top: 0 })
  }

  /** Карточка сотрудника — отдельная страница; новый — `/admins/new`. */
  const openCard = (id: number | 'new') => {
    void navigate(`/admins/${id}`)
  }

  function confirmRemove(person: Staff) {
    remove.mutate(person.id, {
      onSuccess: () => {
        setRemoving(null)
        onToast(`${person.name} удалён — вход в админку закрыт`)
      },
      onError: (error) => {
        setRemoving(null)
        onToast(failureText(error))
      },
    })
  }

  /**
   * Сохранение формы сотрудника. Что получилось, берём из ответа сервера —
   * он и есть источник правды о роли: подписывать сообщение по выбранному
   * пункту значит рассказывать о том, чего сервер мог не принять.
   */
  function apply(person: Staff, change: Partial<Staff>, message: (saved: Staff) => string) {
    const next = { ...person, ...change }
    save.mutate(
      {
        id: next.id,
        name: next.name,
        phone: next.phone,
        email: next.email,
        roleId: next.roleId,
        storeId: next.storeId,
        active: next.active,
      },
      {
        onSuccess: ({ staff: saved }) => {
          onToast(message(saved))
        },
        onError: (error) => {
          onToast(failureText(error))
        },
      },
    )
  }

  function toggleActive(person: Staff) {
    const active = !person.active
    apply(person, { active }, (saved) =>
      active ? `${saved.name} снова может входить` : `${saved.name} больше не войдёт в админку`,
    )
  }

  function resetPassword(person: Staff) {
    reset.mutate(person.id, {
      onSuccess: (password) => {
        setIssued({ name: person.name, password })
      },
      onError: (error) => {
        onToast(failureText(error))
      },
    })
  }

  const allRoles = (roles.data?.items ?? []).map((role) => ({ value: role.id, label: role.name }))
  const allStores = [
    { value: 0, label: 'Все аптеки' },
    ...(stores.data ?? []).map((store) => ({ value: store.id, label: storeTitle(store) })),
  ]

  const storeName = (id: number): string =>
    id ? storeTitleById(stores.data, id, 'аптека') : 'Все аптеки'

  /**
   * Справочник мог не прийти или не знать значение сотрудника — тогда его
   * собственное значение всё равно в списке: иначе поле показало бы чужое.
   */
  function roleOptions(person: Staff): SelectOption<number>[] {
    return allRoles.some((o) => o.value === person.roleId)
      ? allRoles
      : [{ value: person.roleId, label: person.roleName }, ...allRoles]
  }

  function storeOptions(person: Staff): SelectOption<number>[] {
    return allStores.some((o) => o.value === person.storeId)
      ? allStores
      : [...allStores, { value: person.storeId, label: storeName(person.storeId) }]
  }

  const list = staff.data?.items ?? []

  return (
    <>
      <PageActions>
        {staff.data ? (
          <span
            className="ui-count adm-count"
            aria-label={withCountRu(staff.data.total, ['сотрудник', 'сотрудника', 'сотрудников'])}
          >
            {staff.data.total}
          </span>
        ) : null}
        <span className="spacer" />
        {canCreate ? (
          <Press
            as="button"
            type="button"
            className="ui-btn"
            squish="card"
            onPress={() => {
              openCard('new')
            }}
          >
            <Icon name="i-plus" />
            Добавить сотрудника
          </Press>
        ) : null}
      </PageActions>

      <div className="ui-toolbar" role="search">
        <div className="ui-search">
          <Icon name="i-search" />
          <input
            className="ui-input"
            type="search"
            placeholder="Имя, телефон или почта"
            aria-label="Поиск сотрудника"
            value={filters.q}
            onChange={(e) => {
              set({ q: e.target.value })
            }}
          />
        </div>
        <Select
          label="Фильтр по роли"
          value={filters.role}
          active={filters.role !== EMPTY_FILTERS.role}
          options={[{ value: 0, label: 'Все роли' }, ...allRoles]}
          onChange={(role) => {
            set({ role })
          }}
        />
        <Select
          label="Фильтр по аптеке"
          value={filters.store}
          active={filters.store !== EMPTY_FILTERS.store}
          options={allStores}
          onChange={(store) => {
            set({ store })
          }}
        />
        {filtered ? (
          <button type="button" className="ui-btn" data-variant="ghost" onClick={resetFilters}>
            Сбросить фильтры
          </button>
        ) : null}
      </div>

      <section className="ui-table-card" aria-label="Сотрудники">
        {staff.isPending ? <TableSkeleton what="сотрудников" /> : null}
        {staff.isError ? (
          <Failed
            what="сотрудников"
            error={staff.error}
            onRetry={() => {
              void staff.refetch()
            }}
          />
        ) : null}

        {staff.data ? (
          <>
            <div className="ui-table-wrap" aria-busy={staff.isPlaceholderData}>
              <table className="ui-table ui-staff">
                <thead>
                  <tr>
                    <th scope="col">Сотрудник</th>
                    <th scope="col">Контакты</th>
                    <th scope="col">Роль</th>
                    <th scope="col">Аптека</th>
                    <th scope="col">Последний вход</th>
                    <th scope="col">Статус</th>
                    <th scope="col">
                      <span className="visually-hidden">Действия</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="ui-empty">
                          <Icon name="i-search" />
                          <b>Сотрудники не найдены</b>
                          <p>
                            {filters.page > 1
                              ? `На странице ${filters.page} сотрудников нет — список стал короче.`
                              : 'Измените запрос или сбросьте фильтры.'}
                          </p>
                          {filtered ? (
                            <button
                              type="button"
                              className="ui-btn"
                              data-size="sm"
                              data-variant="secondary"
                              onClick={resetFilters}
                            >
                              Сбросить фильтры
                            </button>
                          ) : null}
                          {filters.page > 1 ? (
                            <button
                              type="button"
                              className="ui-btn"
                              data-size="sm"
                              data-variant="secondary"
                              onClick={() => {
                                goTo(1)
                              }}
                            >
                              К первой странице
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                  {list.map((person) => (
                    <StaffRow
                      key={person.id}
                      person={person}
                      self={person.id === me?.id}
                      now={now}
                      canUpdate={canUpdate}
                      canDelete={canDelete}
                      roles={roleOptions(person)}
                      stores={storeOptions(person)}
                      storeName={storeName(person.storeId)}
                      onRole={(roleId) => {
                        apply(person, { roleId }, (saved) => `Роль назначена: ${saved.roleName}`)
                      }}
                      onStore={(storeId) => {
                        apply(person, { storeId }, () =>
                          storeId
                            ? 'Сотрудник закреплён за аптекой: он видит только её заказы'
                            : 'Доступ ко всем аптекам',
                        )
                      }}
                      onOpen={() => {
                        openCard(person.id)
                      }}
                      onToggle={() => {
                        toggleActive(person)
                      }}
                      onReset={() => {
                        resetPassword(person)
                      }}
                      onRemove={() => {
                        setRemoving(person)
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              label="Страницы сотрудников"
              page={filters.page}
              pageSize={filters.size}
              total={staff.data.total}
              sizes={PAGE_SIZES}
              busy={staff.isPlaceholderData}
              onPage={goTo}
              onPageSize={(size) => {
                set({ size: size as PageSize })
              }}
            />
          </>
        ) : null}
      </section>

      {removing ? (
        <Modal
          title="Удалить сотрудника?"
          onClose={() => {
            setRemoving(null)
          }}
        >
          <p>
            {removing.name} больше не войдёт в админку, его сеансы завершатся. Вернуть удалённого
            нельзя — только добавить заново.
          </p>
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                setRemoving(null)
              }}
            >
              Не удалять
            </button>
            <button
              type="button"
              className="ui-btn"
              data-variant="danger"
              disabled={remove.isPending}
              aria-busy={remove.isPending}
              onClick={() => {
                confirmRemove(removing)
              }}
            >
              Удалить
            </button>
          </div>
        </Modal>
      ) : null}

      {issued ? (
        <IssuedPassword
          name={issued.name}
          password={issued.password}
          onClose={() => {
            setIssued(null)
          }}
          onToast={onToast}
        />
      ) : null}
    </>
  )
}

interface StaffRowProps {
  person: Staff
  self: boolean
  now: number
  canUpdate: boolean
  canDelete: boolean
  roles: SelectOption<number>[]
  stores: SelectOption<number>[]
  storeName: string
  onRole: (roleId: number) => void
  onStore: (storeId: number) => void
  onOpen: () => void
  onToggle: () => void
  onReset: () => void
  onRemove: () => void
}

/** Щелчок по полю или кнопке в строке не должен открывать карточку. */
const stay = (event: { stopPropagation: () => void }) => {
  event.stopPropagation()
}

/** Строка сотрудника: кто он, куда входит и что с ним можно сделать. */
function StaffRow({
  person,
  self,
  now,
  canUpdate,
  canDelete,
  roles,
  stores,
  storeName,
  onRole,
  onStore,
  onOpen,
  onToggle,
  onReset,
  onRemove,
}: StaffRowProps) {
  // Владельца и себя отключать и удалять нельзя: иначе можно остаться без доступа.
  const locked = person.system || self
  return (
    // Щелчок по строке открывает карточку сотрудника; поля и кнопки в строке
    // работают сами по себе и строку не открывают.
    <tr className="stf-row" data-href={`/admins/${person.id}`} onClick={onOpen}>
      <td>
        <div className="ui-person">
          <span className="ui-avatar" aria-hidden="true">
            {initials(person.name)}
          </span>
          <span className="txt">
            <span className="primary">{person.name}</span>
            {self || person.system || person.mustChangePassword ? (
              <span className="secondary stf-meta">
                {self ? <span>это вы</span> : person.system ? <span>владелец</span> : null}
                {person.mustChangePassword ? (
                  <span className="ui-tag" data-tone="warning">
                    пароль временный
                  </span>
                ) : null}
              </span>
            ) : null}
          </span>
        </div>
      </td>
      <td>
        <span className="num">{formatPhone(person.phone)}</span>
        <span className="secondary">{person.email || '—'}</span>
      </td>
      <td onClick={stay}>
        {canUpdate && !locked ? (
          <Select
            size="sm"
            label={`Роль сотрудника ${person.name}`}
            value={person.roleId}
            options={roles}
            onChange={onRole}
          />
        ) : (
          <span className="ui-tag">{person.roleName}</span>
        )}
      </td>
      <td onClick={stay}>
        {canUpdate && !person.system ? (
          <Select
            size="sm"
            label={`Аптека сотрудника ${person.name}`}
            value={person.storeId}
            options={stores}
            onChange={onStore}
          />
        ) : (
          storeName
        )}
      </td>
      <td className="num stf-muted">
        {person.lastLogin ? formatDayTime(person.lastLogin) : 'не входил'}
      </td>
      <td>
        <StaffStatus person={person} now={now} />
      </td>
      <td data-align="end" onClick={stay}>
        <span className="ui-actions">
          {canUpdate ? (
            <Press
              as="button"
              type="button"
              className="ui-iconbtn"
              squish="icon"
              aria-label={`Изменить: ${person.name}`}
              data-tip="Изменить"
              data-tip-align="end"
              onPress={onOpen}
            >
              <Icon name="i-edit" />
            </Press>
          ) : null}
          {canUpdate && !locked ? (
            <Press
              as="button"
              type="button"
              className="ui-iconbtn"
              squish="icon"
              aria-label={`${person.active ? 'Отключить доступ' : 'Вернуть доступ'}: ${person.name}`}
              data-tip={person.active ? 'Отключить доступ' : 'Вернуть доступ'}
              data-tip-align="end"
              onPress={onToggle}
            >
              <Icon name={person.active ? 'i-exit' : 'i-check'} />
            </Press>
          ) : null}
          {canUpdate && !person.system ? (
            <Press
              as="button"
              type="button"
              className="ui-iconbtn"
              squish="icon"
              aria-label={`Сбросить пароль: ${person.name}`}
              data-tip="Сбросить пароль"
              data-tip-align="end"
              onPress={onReset}
            >
              <Icon name="i-key" />
            </Press>
          ) : null}
          {canDelete && !locked ? (
            <Press
              as="button"
              type="button"
              className="ui-iconbtn"
              squish="icon"
              aria-label={`Удалить: ${person.name}`}
              data-tip="Удалить"
              data-tip-align="end"
              onPress={onRemove}
            >
              <Icon name="i-trash" />
            </Press>
          ) : null}
        </span>
      </td>
    </tr>
  )
}
